import React, { useEffect, useState } from 'react';
import { Alert, Autocomplete, Box, Button, Chip, CircularProgress, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, isValid, parseISO } from 'date-fns';
import ro from 'date-fns/locale/ro';
import BackofficeReportsApi from './backofficeReportsApi';

const TIMEZONE = 'Europe/Bucharest';
const MIN_DATE = '2000-01-01';
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const dayTime = (value) => DATE_PATTERN.test(value) ? Date.parse(`${value}T00:00:00Z`) : NaN;
const validDate = (value) => Number.isFinite(dayTime(value)) && new Date(dayTime(value)).toISOString().slice(0, 10) === value;
const shiftDate = (value, days) => new Date(dayTime(value) + days * 86400000).toISOString().slice(0, 10);
export const formatItemDate = (value) => validDate(value) ? value.split('-').reverse().join('.') : '—';

export function bucharestToday(now = new Date()) {
  const parts = new Intl.DateTimeFormat('en', { timeZone: TIMEZONE, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(now);
  return ['year', 'month', 'day'].map((key) => parts.find((part) => part.type === key).value).join('-');
}

export function validateItemDate(location, date, today = bucharestToday()) {
  if (!location) return 'Select a location.';
  if (!validDate(date)) return 'Enter a valid date.';
  if (date < MIN_DATE) return 'Select a date from 2000 onward.';
  if (date >= today) return 'Select a completed date through yesterday in Europe/Bucharest.';
  return '';
}

const numeric = (value) => typeof value === 'string' && /^\d+\.\d{4}$/.test(value) && Number.isFinite(Number(value));
const quantityValid = (value) => Number.isSafeInteger(value) && value >= 0;
const metricsValid = (row) => row && ['available', 'not_available'].includes(row.status)
  && (row.reason === null || typeof row.reason === 'string') && typeof row.includes_estimates === 'boolean'
  && (row.sales === null || numeric(row.sales)) && (row.item_quantity === null || quantityValid(row.item_quantity))
  && (row.status === 'available'
    ? numeric(row.sales) && row.item_quantity > 0 && numeric(row.average_sales_per_item)
    : row.average_sales_per_item === null);

function validReport(data, query) {
  return data?.location === query.location && data.dateFrom === query.dateFrom && data.dateTo === query.dateTo
    && data.timezone === TIMEZONE && data.currency === 'RON' && data.delivery === query.delivery
    && ['category', 'subcategory', 'product_class'].every((key) => (data[key] || '') === (query[key] || ''))
    && data.max_period_days === 1
    && Number.isFinite(Date.parse(data.generated_at)) && metricsValid(data.summary)
    && Array.isArray(data.days) && data.days.length === 1
    && data.days.every((row) => row.date === query.dateFrom && metricsValid(row)
      && [null, 'mixed', 'order_items', 'legacy_history_products'].includes(row.evidence?.source))
    && data.evidence?.item_date_basis === 'Order.createdOn:Europe/Bucharest';
}

const reasons = {
  no_sales_data: 'No sales data for this selection.',
  filtered_item_sales_do_not_reconcile: 'Item sales could not be verified against the daily totals.',
  missing_item_classification: 'Some items cannot be assigned to a product group.',
  channel_sales_do_not_reconcile: 'Channel sales do not reconcile with the daily sales total.',
  app_client_scope_unavailable: 'App client orders cannot be identified reliably.',
  incomplete_order_coverage: 'Order records could not be verified against daily transactions.',
  missing_sales_evidence: 'Sales data is missing for part of this selection.',
  invalid_sales_evidence: 'Sales data could not be validated.',
  invalid_transaction_evidence: 'Transaction totals could not be validated.',
  missing_item_evidence: 'Item quantities are missing.',
  invalid_item_evidence: 'Item sales or quantities could not be validated.',
  missing_pos_mapping: 'No tills are mapped to this location.',
  zero_item_quantity: 'No counted items; an average cannot be calculated.',
};
const reasonText = (reason) => reasons[reason] || 'Item analysis is unavailable for this date.';
const amount = (value) => value === null ? '—' : Number(value).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const units = (value) => value === null ? '—' : value.toLocaleString('en-GB');

function errorText(error) {
  if (error?.status === 401) return 'Your session expired. Sign in again.';
  if (error?.status === 403) return 'You do not have permission to view this location.';
  if (error?.status === 404) return 'This location is no longer available.';
  if (error?.status === 400) return 'The selected filters were rejected. Check the date and selected filters.';
  if (error?.message === 'Invalid report response') return 'The report response is incomplete. Retry to load it again.';
  if (error?.status >= 500) return 'The report service failed. Please retry.';
  return 'The report could not be loaded. Check your connection and retry.';
}

export default function AverageSalesPerItemReport({ client }) {
  const [locationData, setLocationData] = useState(null);
  const locations = locationData && locationData.client === client ? locationData.rows : [];
  const [location, setLocation] = useState('0');
  const [delivery, setDelivery] = useState(2);
  const [catalog, setCatalog] = useState(null);
  const [catalogError, setCatalogError] = useState('');
  const [catalogRetry, setCatalogRetry] = useState(0);
  const [productSelection, setProductSelection] = useState({ client, category: '', subcategory: '', product_class: '' });
  const { category, subcategory, product_class: productClass } = productSelection.client === client
    ? productSelection : { category: '', subcategory: '', product_class: '' };
  const options = catalog?.client === client ? catalog.data : null;
  const selectProduct = (changes) => setProductSelection((current) => ({ ...current, client, ...changes }));
  const clearFilters = () => {
    setLocation('0');
    setDelivery(2);
    setProductSelection({ client, category: '', subcategory: '', product_class: '' });
  };
  const [locationsLoading, setLocationsLoading] = useState(true);
  const [locationError, setLocationError] = useState('');
  const [retry, setRetry] = useState(0);
  const [reportRetry, setReportRetry] = useState(0);
  const [selectedDate, setSelectedDate] = useState(() => parseISO(shiftDate(bucharestToday(), -1)));
  // The picker edits a local calendar date; the API keeps its ISO business day.
  const date = selectedDate && isValid(selectedDate) ? format(selectedDate, 'yyyy-MM-dd') : '';
  const [report, setReport] = useState(null);
  const validation = validateItemDate(location, date);
  const canLoad = !locationsLoading && !locationError && locations.length > 0 && !validation;
  const queryKey = JSON.stringify([date, location, delivery, category, subcategory, productClass, retry, reportRetry]);
  const currentReport = canLoad && report && report.client === client && report.key === queryKey ? report : null;
  const result = currentReport?.data;
  const error = currentReport?.error;
  const loading = canLoad && !currentReport;

  useEffect(() => {
    let active = true;
    setLocationData(null);
    setLocation('0');
    setLocationsLoading(true);
    setLocationError('');
    const request = BackofficeReportsApi.getLocationsAsync(client);
    const timer = setTimeout(() => {
      if (active) {
        active = false;
        setLocationsLoading(false);
        setLocationError('Locations timed out. Retry to load them again.');
        request?.abort?.();
      }
    }, 30000);
    Promise.resolve(request).then((data) => {
      const rows = Array.isArray(data) ? data : data?.locations;
      if (!Array.isArray(rows) || !rows.every((row) => typeof row?.iref === 'string' && typeof row.name === 'string')) {
        throw new Error('Invalid locations response');
      }
      if (active) {
        const selectable = rows.filter((row) => row.iref && row.iref !== '0');
        setLocationData({ client, rows: selectable.length ? [{ iref: '0', name: 'All locations' }, ...selectable] : [] });
        setLocation((current) => current === '0' || selectable.some((row) => row.iref === current) ? current : '0');
      }
    }).catch((failure) => {
      if (active) setLocationError(failure?.status === 401 || failure?.status === 403 ? errorText(failure) : 'Locations could not be loaded. Please retry.');
    }).finally(() => { clearTimeout(timer); if (active) setLocationsLoading(false); });
    return () => {
      active = false;
      clearTimeout(timer);
      request?.abort?.();
    };
  }, [client, retry]);

  useEffect(() => {
    let active = true;
    let request;
    setCatalog(null);
    setCatalogError('');
    setProductSelection({ client, category: '', subcategory: '', product_class: '' });
    const timer = setTimeout(() => {
      if (active) {
        active = false;
        setCatalogError('Product filters timed out.');
        request?.abort?.();
      }
    }, 30000);
    try {
      request = BackofficeReportsApi.getAverageSalesPerItemOptionsAsync(client);
    } catch (failure) {
      request = Promise.reject(failure);
    }
    Promise.resolve(request).then((data) => {
      if (!['categories', 'subcategories', 'classes'].every((key) => Array.isArray(data?.[key])
        && data[key].every((row) => typeof row?.iref === 'string' && !!row.iref && typeof row.name === 'string'))
        || !data.subcategories.every((row) => data.categories.some((parent) => parent.iref === row.category_id))) {
        throw new Error('Invalid product filters');
      }
      if (active) setCatalog({ client, data });
    }).catch(() => {
      if (active) setCatalogError('Product filters could not be loaded.');
    }).finally(() => clearTimeout(timer));
    return () => { active = false; clearTimeout(timer); request?.abort?.(); };
  }, [client, catalogRetry]);

  useEffect(() => {
    if (!canLoad) return undefined;
    let active = true;
    const query = { location, dateFrom: date, dateTo: date, delivery,
      ...(category ? { category } : {}), ...(subcategory ? { subcategory } : {}),
      ...(productClass ? { product_class: productClass } : {}),
    };
    setReport(null);
    // Keep synchronous transport errors in the same visible error path.
    let request;
    try {
      request = BackofficeReportsApi.getAverageSalesPerItemAsync(client, query);
    } catch (failure) {
      setReport({ client, key: queryKey, error: errorText(failure) });
      return undefined;
    }
    const timer = setTimeout(() => {
      if (active) {
        active = false;
        setReport({ client, key: queryKey, error: 'The report timed out. Please retry.' });
        request?.abort?.();
      }
    }, 30000);
    Promise.resolve(request).then((data) => {
      if (!active) return;
      if (!validReport(data, query)) throw new Error('Invalid report response');
      setReport({ client, key: queryKey, data });
    }).catch((failure) => {
      if (active) setReport({ client, key: queryKey, error: errorText(failure) });
    }).finally(() => clearTimeout(timer));
    return () => {
      active = false;
      clearTimeout(timer);
      request?.abort?.();
    };
  }, [client, location, date, delivery, category, subcategory, productClass, canLoad, queryKey]);

  const navigateDate = (value) => {
    setSelectedDate(parseISO(value));
  };
  const filterSx = {
    '& .MuiInputBase-root': { height: 36, borderRadius: '12px', bgcolor: '#fff', fontSize: { xs: '0.82rem', sm: '0.88rem' } },
    '& .MuiInputBase-input': { fontSize: { xs: '16px', sm: '0.88rem' }, py: 0.35 },
    '& .MuiInputLabel-root': { fontSize: { xs: '0.76rem', sm: '0.82rem' } },
  };
  const selectedLocation = locations.find((row) => row.iref === location) || null;
  const yesterday = shiftDate(bucharestToday(), -1);
  const help = 'Gross sales divided by items sold on the selected day. Each combo unit counts as one item. Only completed orders count. Dates use Europe/Bucharest.';
  const summaryHelp = result ? `${help} Based on ${units(result.summary.item_quantity)} items and ${amount(result.summary.sales)} RON in recorded sales.` : help;
  const incompleteLocations = result?.summary.reason === 'incomplete_order_coverage'
    ? locations.filter((row) => row.iref !== '0'
      && result.days[0].evidence?.locations?.[row.iref]?.reason === 'incomplete_order_coverage').map((row) => row.name)
    : [];
  const unavailableText = result?.location === '0' && result.summary.reason === 'incomplete_order_coverage'
    ? 'Order records could not be verified for one or more locations. The combined average is unavailable.'
    : reasonText(result?.summary.reason);
  const dateNavigationButtonSx = {
    width: { xs: 40, sm: 36 }, height: { xs: 40, sm: 36 }, p: 0, flexShrink: 0,
    color: '#94a3b8', '&:hover': { color: '#1a237e', bgcolor: 'rgba(26,35,126,0.05)' },
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', flex: 1, minHeight: 0, bgcolor: '#fff' }}>
      <Box sx={{ px: { xs: 0.75, sm: 1 }, py: { xs: 1, sm: 1.25 }, flexShrink: 0,
        borderBottom: '1px solid rgba(15,23,42,0.06)', background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)' }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' },
          alignItems: { xs: 'stretch', lg: 'flex-start' }, justifyContent: 'space-between', gap: 1.5 }}>
          <Box sx={{ minWidth: 0, pt: { lg: 0.25 } }}>
            <Typography component="h2" sx={{ m: 0, color: '#1a237e', fontWeight: 800,
              fontSize: { xs: '0.9rem', sm: '1rem' }, lineHeight: 1.2 }}>
              Sales per item
            </Typography>
            <Typography sx={{ mt: 0.15, color: '#64748b', fontWeight: 600,
              fontSize: { xs: '0.72rem', sm: '0.78rem' }, lineHeight: 1.2 }}>
              Daily sales and item counts
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0,
            width: { xs: '100%', lg: 680 }, maxWidth: '100%', gap: 1.25 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75,
              justifyContent: { xs: 'flex-start', lg: 'flex-end' } }}>
              <Box role="group" aria-label="Report date navigation" sx={{ display: 'flex', alignItems: 'center', gap: 0.25,
                flex: { xs: '1 1 100%', sm: '0 0 auto' } }}>
          <Tooltip title="Previous day"><span><IconButton aria-label="Previous day" sx={dateNavigationButtonSx}
            disabled={!!validation || date <= MIN_DATE} onClick={() => navigateDate(shiftDate(date, -1))}>
            <ChevronLeftRoundedIcon fontSize="small" />
          </IconButton></span></Tooltip>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ro}>
            <DatePicker label="Date" inputFormat="dd.MM.yyyy" value={selectedDate} onChange={(value) => setSelectedDate(value)}
              minDate={parseISO(MIN_DATE)} maxDate={parseISO(yesterday)}
              renderInput={(params) => <TextField {...params} size="small" InputLabelProps={{ shrink: true }}
                sx={{ width: 170, ...filterSx }} error={!!validation} helperText={validation || undefined} />} />
          </LocalizationProvider>
          <Tooltip title="Next day"><span><IconButton aria-label="Next day" sx={dateNavigationButtonSx}
            disabled={!!validation || date >= yesterday} onClick={() => navigateDate(shiftDate(date, 1))}>
            <ChevronRightRoundedIcon fontSize="small" />
          </IconButton></span></Tooltip>
          <Tooltip title="Go to yesterday"><span><IconButton aria-label="Go to yesterday" sx={dateNavigationButtonSx}
            disabled={date === yesterday} onClick={() => navigateDate(yesterday)}>
            <HomeRoundedIcon fontSize="small" />
          </IconButton></span></Tooltip>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', minWidth: 0, flex: { xs: '1 1 100%', sm: '0 1 auto' } }}>
          <Autocomplete options={locations} value={selectedLocation} disableClearable
            getOptionLabel={(option) => option.name} isOptionEqualToValue={(a, b) => a.iref === b.iref}
            onChange={(_, option) => setLocation(option?.iref || '0')} disabled={locationsLoading || !!locationError}
            sx={{ minWidth: 0, flex: 1, width: { sm: 230 }, maxWidth: { sm: 230 }, ...filterSx, '& .MuiAutocomplete-inputRoot': { py: 0 } }}
            renderInput={(params) => <TextField {...params} label="Location" size="small" />} />
          <Tooltip title="App Clients include identified app customers in both Restaurant and Delivery sales.">
            <TextField select SelectProps={{ native: true }} label="Sales" size="small" value={delivery}
              sx={{ width: 125, flexShrink: 0, ...filterSx }} onChange={(event) => setDelivery(Number(event.target.value))}>
              {[[2, 'All'], [0, 'Restaurant'], [1, 'Delivery'], [3, 'App Clients']].map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </TextField>
          </Tooltip>
        </Box>
            </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center',
          justifyContent: { xs: 'flex-start', lg: 'flex-end' } }}>
          <TextField select SelectProps={{ native: true }} label="Category" InputLabelProps={{ shrink: true }} size="small" value={category}
            disabled={!options} sx={{ flex: { xs: '1 1 135px', sm: '0 1 164px' }, minWidth: 0, ...filterSx }}
            onChange={(event) => selectProduct({ category: event.target.value, subcategory: '' })}>
            <option value="">All categories</option>
            {options?.categories.map((row) => <option key={row.iref} value={row.iref}>{row.name}</option>)}
          </TextField>
          <TextField select SelectProps={{ native: true }} label="Subcategory" InputLabelProps={{ shrink: true }} size="small" value={subcategory}
            disabled={!options || !category} sx={{ flex: { xs: '1 1 135px', sm: '0 1 164px' }, minWidth: 0, ...filterSx }}
            onChange={(event) => selectProduct({ subcategory: event.target.value })}>
            <option value="">All subcategories</option>
            {options?.subcategories.filter((row) => row.category_id === category).map((row) => <option key={row.iref} value={row.iref}>{row.name}</option>)}
          </TextField>
          <TextField select SelectProps={{ native: true }} label="Class" InputLabelProps={{ shrink: true }} size="small" value={productClass}
            disabled={!options} sx={{ flex: { xs: '1 1 135px', sm: '0 1 164px' }, minWidth: 0, ...filterSx }}
            onChange={(event) => selectProduct({ product_class: event.target.value })}>
            <option value="">All classes</option>
            {options?.classes.map((row) => <option key={row.iref} value={row.iref}>{row.name}</option>)}
          </TextField>
          <Tooltip title="Reset all filters; keep the selected date" arrow><Box component="span" sx={{ display: 'inline-flex', flexShrink: 0 }}>
            <Button size="small" variant="outlined" startIcon={<RestartAltRoundedIcon />} disabled={location === '0' && delivery === 2 && !category && !subcategory && !productClass}
              sx={{
                height: 36, px: 1.5, borderRadius: '12px', textTransform: 'none', whiteSpace: 'nowrap',
                fontSize: '0.78rem', fontWeight: 600, color: '#1a237e',
                borderColor: 'rgba(26,35,126,0.18)', bgcolor: 'rgba(26,35,126,0.035)',
                '& .MuiButton-startIcon': { mr: 0.6 },
                '& .MuiButton-startIcon > *:nth-of-type(1)': { fontSize: 18 },
                '&:hover': { borderColor: 'rgba(26,35,126,0.35)', bgcolor: 'rgba(26,35,126,0.07)' },
                '&.Mui-focusVisible': { outline: '2px solid #1a237e', outlineOffset: 2 },
                '&.Mui-disabled': { color: '#94a3b8', borderColor: 'rgba(148,163,184,0.2)', bgcolor: '#f8fafc' },
              }}
              onClick={clearFilters}>
              Clear filters
            </Button>
          </Box></Tooltip>
          <Tooltip arrow title="Filters use the current product catalog, including for past dates. Combos use their own category and class and count as one item per combo unit. Sales and items both follow your selection.">
            <InfoOutlinedIcon tabIndex={0} aria-label="About product filters" sx={{ fontSize: 17, color: '#94a3b8' }} />
          </Tooltip>
        </Box>
          </Box>
        </Box>
        {catalogError && <Alert sx={{ mt: 1 }} severity="warning" action={<Button onClick={() => setCatalogRetry((value) => value + 1)}>Retry filters</Button>}>{catalogError}</Alert>}
      </Box>
      <Box sx={{ p: { xs: 1, sm: 1.5 }, flex: 1, minHeight: 0, overflow: 'auto', bgcolor: '#f8fafc' }}>
        {locationsLoading && <CircularProgress size={22} aria-label="Loading locations" />}
        {locationError && <Alert severity="error" action={<Button onClick={() => setRetry((value) => value + 1)}>Retry</Button>}>{locationError}</Alert>}
        {!locationsLoading && !locationError && !locations.length && <Typography color="text.secondary">No locations are available for your account.</Typography>}
        {error && <Alert severity="error" action={<Button onClick={() => setReportRetry((value) => value + 1)}>Retry</Button>}>{error}</Alert>}
        {loading && <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={22} aria-label="Loading report" /></Box>}
        {result && <>
          <Box sx={{ textAlign: 'center', p: { xs: 2, sm: 3 }, maxWidth: 640, mx: 'auto',
            border: '1px solid rgba(15,23,42,0.08)', borderRadius: '12px', bgcolor: '#fff' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
              <Typography sx={{ color: '#64748b', fontSize: '0.85rem', fontWeight: 650 }}>Average sales per item</Typography>
              <Tooltip title={summaryHelp} arrow><InfoOutlinedIcon tabIndex={0} aria-label="About average sales per item" sx={{ fontSize: 17, color: '#94a3b8' }} /></Tooltip>
            </Box>
            <Typography sx={{ color: '#1a237e', fontSize: { xs: '2rem', sm: '2.4rem' }, fontWeight: 750, fontVariantNumeric: 'tabular-nums', lineHeight: 1.4 }}>
              {amount(result.summary.average_sales_per_item)} <Box component="span" sx={{ fontSize: '0.8rem', color: '#64748b', fontWeight: 500 }}>RON/item</Box>
            </Typography>
            <Typography variant="caption" color="text.secondary">{formatItemDate(result.dateFrom)}</Typography>
            <Box component="dl" sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 2, mx: 'auto', mt: 2, mb: 0, maxWidth: 420 }}>
              <Box>
                <Typography component="dt" variant="caption" color="text.secondary">Total sales</Typography>
                <Typography component="dd" aria-label="Total sales" sx={{ m: 0, fontSize: '1.05rem', fontWeight: 650, fontVariantNumeric: 'tabular-nums' }}>
                  {amount(result.summary.sales)} <Box component="span" sx={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 400 }}>RON</Box>
                </Typography>
              </Box>
              <Box>
                <Typography component="dt" variant="caption" color="text.secondary">Items sold</Typography>
                <Typography component="dd" aria-label="Items sold" sx={{ m: 0, fontSize: '1.05rem', fontWeight: 650, fontVariantNumeric: 'tabular-nums' }}>
                  {units(result.summary.item_quantity)}
                </Typography>
              </Box>
            </Box>
            {result.summary.status === 'available' && result.summary.includes_estimates && <Box sx={{ mt: 0.75 }}><Tooltip arrow title="Some item counts come from older product history because the original orders are unavailable. That history may not preserve a combo as one item, so this average is an estimate."><Chip tabIndex={0} label="Estimated average" size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: 22, color: '#64748b' }} /></Tooltip></Box>}
            {result.summary.status !== 'available' && <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {unavailableText}
              {incompleteLocations.length > 0 && <Tooltip arrow title={`Locations with unverified order records: ${incompleteLocations.join(', ')}.`}>
                <IconButton size="small" aria-label="Locations with unverified order records" sx={{ ml: 0.25, p: 0.25, verticalAlign: 'middle' }}>
                  <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>}
            </Typography>}
          </Box>
        </>}
      </Box>
    </Box>
  );
}
